import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CalendarRange, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { hoyEnZona } from '@/lib/timezone';
import type { ReportFiltros } from '@/types/reports';
import {
  CHANNEL_FILTROS,
  EMPTY_REPORT_FILTROS,
  RANGOS_RAPIDOS,
  applyReportFiltros,
  conParametro,
  describirFiltros,
  hayFiltros,
  rangoActivo,
  rangoInvertido,
  rangoRapido,
  reportFiltrosFrom,
  type RangoRapido,
} from './reports-format';
import { useZonaHorariaReportes } from './use-reports';

/**
 * Filtros de los reportes, sincronizados con la URL.
 *
 * Por qué la URL y no un `useState`: el panel y los reportes se comparten por enlace («mira el CPA
 * de esta semana») y el botón «atrás» tiene que devolver el rango anterior. Es el mismo criterio del
 * inbox, que guarda la conversación abierta en `?conversacion=`.
 *
 * El hook `useReportFiltros` hace la lectura/escritura y la pantalla solo pinta; `ReportFilters` es la
 * barra visual. Los cinco parámetros son los del backend (`desde`, `hasta`, `channel`, `adId`,
 * `campaignId`) con las fechas como DÍAS de calendario: la conversión a instantes ISO en la zona del
 * negocio la hace `reportQueryFrom` al construir la query (`@/api/reports`).
 */

export interface ReportFiltrosState {
  filtros: ReportFiltros;
  /** Rango rápido que coincide con las fechas puestas (`null` = rango a mano o sin fechas). */
  rango: RangoRapido | null;
  /** Cambia solo los filtros que llegan, borrando de la URL los que queden vacíos. */
  setFiltros: (cambios: Partial<ReportFiltros>) => void;
  /** Cambia un parámetro cualquiera de la pantalla (`intervalo`, `agrupar`…) sin tocar los filtros. */
  setParam: (key: string, value: string | null) => void;
  /** Deja la URL sin filtros (los otros parámetros, como el intervalo, se conservan). */
  resetFiltros: () => void;
  /** Zona del negocio para contar los días (la que reportaron las respuestas). */
  zonaHoraria: string;
}

export function useReportFiltros(): ReportFiltrosState {
  const [searchParams, setSearchParams] = useSearchParams();
  const zonaHoraria = useZonaHorariaReportes();

  const filtros = useMemo(() => reportFiltrosFrom(searchParams), [searchParams]);
  const rango = useMemo(() => rangoActivo(filtros, zonaHoraria), [filtros, zonaHoraria]);

  const write = useCallback(
    (next: URLSearchParams) => {
      // `replace`: teclear un `ad_id` no debe llenar el historial de un paso por pulsación.
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  const setFiltros = useCallback(
    (cambios: Partial<ReportFiltros>) => write(applyReportFiltros(searchParams, cambios)),
    [searchParams, write],
  );

  const setParam = useCallback(
    (key: string, value: string | null) => write(conParametro(searchParams, key, value)),
    [searchParams, write],
  );

  const resetFiltros = useCallback(
    () => write(applyReportFiltros(searchParams, EMPTY_REPORT_FILTROS)),
    [searchParams, write],
  );

  return { filtros, rango, setFiltros, setParam, resetFiltros, zonaHoraria };
}


export interface ReportFiltersProps {
  filtros: ReportFiltros;
  /** Rango rápido activo (de `useReportFiltros`) o `null` si el rango se escribió a mano. */
  rango: RangoRapido | null;
  /** Zona del negocio: con ella se cuentan los días y se avisa en el pie. */
  zonaHoraria: string;
  onChange: (cambios: Partial<ReportFiltros>) => void;
  onReset: () => void;
  /** Controles que van a la derecha de los rangos (intervalo del gráfico, métrica…). */
  actions?: ReactNode;
  className?: string;
}

export function ReportFilters({
  filtros,
  rango,
  zonaHoraria,
  onChange,
  onReset,
  actions,
  className,
}: ReportFiltersProps) {
  const hoy = hoyEnZona(zonaHoraria);
  const invertido = rangoInvertido(filtros);

  return (
    <Card className={className}>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-caption font-medium text-ink-soft">
            <CalendarRange size={15} aria-hidden="true" />
            Periodo
          </span>

          {RANGOS_RAPIDOS.map((opcion) => (
            <Button
              key={opcion.id}
              size="sm"
              variant={rango === opcion.id ? 'subtle' : 'secondary'}
              aria-pressed={rango === opcion.id}
              onClick={() => onChange(rangoRapido(opcion.id, zonaHoraria))}
            >
              {opcion.label}
            </Button>
          ))}

          {hayFiltros(filtros) ? (
            <Button size="sm" variant="ghost" leftIcon={<X size={14} />} onClick={onReset}>
              Quitar los filtros
            </Button>
          ) : null}

          {actions ? (
            <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>
          ) : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Field label="Desde" htmlFor="report-filtro-desde">
            <Input
              id="report-filtro-desde"
              type="date"
              max={hoy}
              value={filtros.desde}
              invalid={invertido}
              onChange={(event) => onChange({ desde: event.target.value })}
            />
          </Field>

          <Field
            label="Hasta"
            htmlFor="report-filtro-hasta"
            hint={`Días del negocio (${zonaHoraria})`}
          >
            <Input
              id="report-filtro-hasta"
              type="date"
              max={hoy}
              value={filtros.hasta}
              invalid={invertido}
              onChange={(event) => onChange({ hasta: event.target.value })}
            />
          </Field>

          <Field label="Canal" htmlFor="report-filtro-channel">
            <Select
              id="report-filtro-channel"
              value={filtros.channel}
              onChange={(event) => onChange({ channel: event.target.value })}
            >
              {CHANNEL_FILTROS.map((opcion) => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </Select>
          </Field>

          <TextoFiltroDebounced
            id="report-filtro-adid"
            label="Anuncio (ad_id)"
            placeholder="Todos los anuncios"
            value={filtros.adId}
            onCommit={(valor) => onChange({ adId: valor })}
          />

          <TextoFiltroDebounced
            id="report-filtro-campana"
            label="Campaña (campaign_id)"
            placeholder="Todas las campañas"
            value={filtros.campaignId}
            onCommit={(valor) => onChange({ campaignId: valor })}
          />
        </div>

        {invertido ? (
          <Alert tone="warning">
            La fecha «desde» es posterior a «hasta»: el backend no va a encontrar nada en ese rango.
            Cambia una de las dos, o elige uno de los rangos de arriba.
          </Alert>
        ) : null}

        <p className="text-caption text-ink-muted">
          {describirFiltros(filtros, zonaHoraria)}
          {hayFiltros(filtros) ? '' : ' — el backend usa los últimos 30 días'}. Los filtros van en la
          URL: puedes copiar el enlace con el rango ya puesto.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Campo de texto que espera a que el admin deje de teclear antes de cambiar la URL (y con eso, la
 * consulta).
 *
 * Sin la espera, escribir un `ad_id` de 15 dígitos dispararía 15 consultas al backend (una por
 * pulsación) y las respuestas podrían llegar desordenadas. El borrador local también resuelve el
 * problema clásico de los filtros en la URL: si el valor de fuera cambia (llegó un enlace compartido,
 * se pulsó «Quitar los filtros»), el campo se sincroniza aunque el admin esté escribiendo.
 */
function TextoFiltroDebounced({
  id,
  label,
  placeholder,
  value,
  onCommit,
  ms = 400,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onCommit: (valor: string) => void;
  ms?: number;
}) {
  const [borrador, setBorrador] = useState(value);

  useEffect(() => {
    setBorrador(value);
  }, [value]);

  // El callback vive en un ref para que el temporizador no se reinicie en cada render del padre.
  const commitRef = useRef(onCommit);
  useEffect(() => {
    commitRef.current = onCommit;
  });

  useEffect(() => {
    if (borrador === value) return undefined;

    const temporizador = setTimeout(() => commitRef.current(borrador.trim()), ms);
    return () => clearTimeout(temporizador);
  }, [borrador, value, ms]);

  return (
    <Field
      label={label}
      htmlFor={id}
      hint={borrador.trim() !== value.trim() ? 'Escribiendo…' : 'Se aplica al dejar de escribir'}
    >
      <Input
        id={id}
        type="search"
        value={borrador}
        placeholder={placeholder}
        onChange={(event) => setBorrador(event.target.value)}
      />
    </Field>
  );
}

